import React, { forwardRef, Fragment, useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import MenuButton from '@polestar/component-warehouse-react/components/menu-button/MenuButton';
import Logo from '@polestar/component-warehouse-react/components/logo/Logo';
import styled from '@emotion/styled';
import Row from '@polestar/component-warehouse-react/components/row/Row';
import { CrossAxisAlignments } from '@polestar/component-warehouse-react/tokens/alignments/Alignments';
import Spacer from '@polestar/component-warehouse-react/components/spacer/Spacer';
import Orientations from '@polestar/component-warehouse-react/tokens/orientations/Orientations';
import { css } from '@emotion/react';
import { responsiveStyles } from '@polestar/component-warehouse-react/helpers/responsiveStyle';
import Spacings, { createCustomSpacing, } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import Section, { sectionContainerPaddings, sectionInnerStyling, } from '@polestar/component-warehouse-react/components/section/Section';
import { createCSSTransition, createTransition, MotionDurations, MotionDurationsSeconds, MotionEasings, } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import { HeaderPositions, useHeader, } from '@polestar/component-warehouse-react/components/header/HeaderContext';
import LayoutIndexes from '@polestar/component-warehouse-react/tokens/layout-indexes/LayoutIndexes';
import { useHeightMotionValueCallback } from '@polestar/component-warehouse-react/hooks/useDimensions';
import { ThemeExtraTypes, ThemeTypes } from '@polestar/component-warehouse-react/themes/Theme';
import { useThemeType } from '@polestar/component-warehouse-react/hooks/useTheme';
import useMutableValue from '@polestar/component-warehouse-react/hooks/useMutableValue';
import { m as motion, useMotionValue, useTransform } from 'framer-motion';
import { ScrollDirection, useContentElement, } from '@polestar/component-warehouse-react/contexts/contentElement';
import { useTransition } from '@polestar/component-warehouse-react/components/gallery/Gallery.helpers';
import framesync from 'framesync';
import useOnValuesChange from '@polestar/component-warehouse-react/hooks/useOnValuesChange';
import { ScrolledPassedHelper } from '@polestar/component-warehouse-react/components/header/Header.helpers';
import { SwitchableThemeProvider } from '@polestar/component-warehouse-react/components/theme-providers/ThemeProviders';
import DeviceTypes from '@polestar/component-warehouse-react/tokens/device-types/DeviceTypes';
import useMediaQuery from '@polestar/component-warehouse-react/hooks/useMediaQuery';
import { shouldNotForwardProps } from '@polestar/component-warehouse-react/helpers/shouldForwardProps';
import OfferBar from '@polestar/component-warehouse-react/components/offer-bar/OfferBar';
import { useInteractionMode } from '@polestar/component-warehouse-react/contexts/interactionMode';
export var HeaderVariants;
(function (HeaderVariants) {
    HeaderVariants["static"] = "static";
    HeaderVariants["default"] = "default";
    HeaderVariants["transparent"] = "transparent";
    HeaderVariants["alwaysVisible"] = "alwaysVisible";
})(HeaderVariants || (HeaderVariants = {}));
const scrollDeltaThreshold = {
    [DeviceTypes.mobile]: 50,
    default: 150,
};
const Header = forwardRef(({ logoProps = {}, menuButton = true, menuButtonProps, variant = HeaderVariants.default, onFocus, actions, offerBarProps, subNavigation = [], ...props }, ref) => {
    var _a;
    const { headerVisible, headerPosition, headerHeight: _headerHeight, baseHeaderHeight, setSafeAreaHeight, headerTheme, showHeader, setHeaderTheme, setOfferBarHeight, topOfPageTheme, setHeaderVariant, setSubNavigationSafeAreaHeight, } = useHeader();
    const isStatic = variant === HeaderVariants.static;
    const isAlwaysVisible = variant === HeaderVariants.alwaysVisible;
    const isTransparent = variant === HeaderVariants.transparent;
    const { name: mediaQueryName } = useMediaQuery();
    const { scrollDirection, scrollY, scrollYDelta } = useContentElement();
    const [callbackRef, headerHeight] = useHeightMotionValueCallback();
    const [subNavigationRef, subNavigationHeight] = useHeightMotionValueCallback();
    const [offerBarRef, offerBarHeight] = useHeightMotionValueCallback();
    const [offsetTopRef, offsetTopHeight] = useHeightMotionValueCallback();
    const [offsetBottomRef, offsetBottomHeight] = useHeightMotionValueCallback();
    const scrollYReached = useMutableValue(false);
    const [shouldRenderActions, setShouldRenderActions] = useState(headerVisible.get());
    const { isKeyboardControlled } = useInteractionMode();
    const hidden = useRef(false);
    /**
     * due to the space-keeper and the hiding of the header the `scroll-padded-top` sometimes needs another position to prevent appearing below the scroll-padded-bottom which will trigger looping of the Header up/down.
     * These 2 values are needed to be able to calculate on the right moments the position where it needs to go to and the final position
     */
    const scrollPassedTopOffset = useMotionValue('0');
    useEffect(() => {
        // only to be able to share this with other components for specific scrolling use-cases
        setHeaderVariant(variant);
    }, [setHeaderVariant, variant]);
    useEffect(() => scrollYDelta.on('change', (latestScrollYDelta) => {
        if (latestScrollYDelta >
            (scrollDeltaThreshold[mediaQueryName] || scrollDeltaThreshold.default)) {
            scrollYReached.set(true);
        }
    }), [scrollYDelta, scrollYReached, mediaQueryName]);
    useEffect(() => scrollDirection.on('change', () => scrollYReached.set(false)), [scrollDirection, scrollYDelta, scrollYReached]);
    useEffect(() => {
        if (scrollY.get() === 0) {
            setHeaderTheme(topOfPageTheme.get());
        }
        return scrollY.on('change', (latestScrollY) => {
            if (latestScrollY === 0) {
                setHeaderTheme(topOfPageTheme.get());
            }
        });
    }, [scrollY, setHeaderTheme, topOfPageTheme]);
    const topPassed = useMutableValue(false);
    const topOffsetRef = useRef(0);
    const bottomPassed = useMutableValue(false);
    const bottomOffsetRef = useRef(0);
    const makeHidden = useMotionValue(false);
    const position = useMotionValue(HeaderPositions.static);
    const fixedPositionOffset = useMotionValue(position.get() === HeaderPositions.fixed ? 'block' : 'none');
    /**
     * This y-offset is necessary for when the menu-part needs to be hidden during scrolling
     */
    const containerYOffset = useMotionValue('0');
    /**
     * Update context
     */
    useEffect(() => position.on('change', (latestPosition) => {
        return headerPosition.set(latestPosition);
    }), [headerPosition, position]);
    useEffect(() => headerVisible.on('change', (latestHeaderVisible) => {
        // TODO: place somewhere else? or remove?
        !!actions && setShouldRenderActions(latestHeaderVisible);
    }), [actions, headerVisible]);
    const allowEvents = useMotionValue(true);
    const transition = createTransition({
        duration: MotionDurationsSeconds.fast,
        onPlay: () => {
            allowEvents.set(true);
        },
        onComplete: () => {
            framesync.update(() => {
                allowEvents.set(isAlwaysVisible ||
                    position.get() === HeaderPositions.static ||
                    (position.get() === HeaderPositions.fixed && !makeHidden.get()));
            }, false, true);
        },
    });
    /**
     * When the Header gets "fixed" a container is needed to push the content below to ensure that there is no visual jump of the content
     */
    const SpaceKeeper = motion.div;
    const spaceKeeperHeight = useTransform([headerHeight, subNavigationHeight], ([headerHeightValue, subNavigationHeightValue]) => isTransparent ? 0 : headerHeightValue + subNavigationHeightValue);
    // background of the menu part of the Header to hide initially at the transparent variant
    // but show when scrolling to ensure all is visible whatever the content is behind it
    const backgroundOpacity = useMotionValue(0);
    const { animatedMotionValue: animatedBackgroundOpacity, directlySetOnce: directlySetOnceOpacity, } = useTransition(backgroundOpacity, transition);
    // determine when passed top/bottom thresholds what to change
    const handleValueChange = useCallback((latestTopPassed, latestBottomPassed, latestScrollDirection, latestScrollYReached) => {
        if (isStatic) {
            // Reset, when going from non-static to static header
            makeHidden.set(false);
            position.set(HeaderPositions.static);
            return;
        }
        let newBackgroundOpacity;
        let newPosition;
        let newMakeHidden;
        // scrollTop in between top and bottom
        if (latestTopPassed && !latestBottomPassed && !isAlwaysVisible) {
            if (makeHidden.get()) {
                newPosition = HeaderPositions.static;
                newMakeHidden = false;
                newBackgroundOpacity = 0;
            }
        }
        else if (!latestTopPassed) {
            // TODO: breaks going down but maybe still good to use
            // ensure that at top of the page is always static
            if (position.get() !== HeaderPositions.static) {
                newPosition = HeaderPositions.static;
            }
            newMakeHidden = false;
            newBackgroundOpacity = 0;
        }
        else if (latestBottomPassed || (latestTopPassed && isAlwaysVisible)) {
            // scrollTop below bottom
            if (position.get() !== HeaderPositions.fixed) {
                newPosition = HeaderPositions.fixed;
                newMakeHidden = true;
            }
            newBackgroundOpacity = 1;
            // Hide/show on scroll, but only when scroll has been reached
            if (latestScrollYReached) {
                newMakeHidden = latestScrollDirection === ScrollDirection.down;
            }
        }
        /**
         * Assume at the top of the page, reset
         */
        if ((scrollY.get() === 0 ||
            // only trigger near the top and not in the middle of the page #resize-bug
            (scrollY.get() < topOffsetRef.current && !latestScrollYReached)) &&
            position.get() !== HeaderPositions.static) {
            newPosition = HeaderPositions.static;
            newMakeHidden = false;
            newBackgroundOpacity = 0;
        }
        let skipPositionSet = false;
        if (newMakeHidden !== undefined) {
            /**
             * If it goes from invisible+fixed => visible+static
             * This happens on navigation where a routing library does window.scrollTo(0,0)
             */
            if (newPosition === HeaderPositions.static &&
                ((position.get() === HeaderPositions.fixed && makeHidden.get()) ||
                    (position.get() === HeaderPositions.fixed && makeHidden.get()))) {
                directlySetOnceOpacity(newBackgroundOpacity);
            }
        }
        if (newPosition !== undefined) {
            /**
             * If it goes from visible+static => invisible+fixed
             * This happens when scrolling past the top down
             */
            if (latestScrollDirection === ScrollDirection.down &&
                position.get() === HeaderPositions.static) {
                skipPositionSet = true;
                directlySetOnceOpacity(newBackgroundOpacity);
                framesync.update(() => {
                    if (newPosition) {
                        position.set(newPosition);
                        // TODO: wrong position?
                        fixedPositionOffset.set(newPosition === HeaderPositions.fixed ? 'block' : 'none');
                    }
                }, false, true);
            }
        }
        // ensure that when isTransparent the extra internal offset is removed when the menu-part becomes "static"
        if (!latestTopPassed && !latestBottomPassed) {
            const currentOffset = containerYOffset.get();
            if (Number(currentOffset.replace('px', '')) !== 0) {
                containerYOffset.set('0');
            }
        }
        if (newMakeHidden !== undefined) {
            makeHidden.set(newMakeHidden);
        }
        if (newBackgroundOpacity !== undefined) {
            backgroundOpacity.set(newBackgroundOpacity);
        }
        // only execute when framesync is not initiated
        if (!skipPositionSet && newPosition !== undefined) {
            position.set(newPosition);
            fixedPositionOffset.set(newPosition === HeaderPositions.fixed ? 'block' : 'none');
        }
    }, [
        backgroundOpacity,
        directlySetOnceOpacity,
        isAlwaysVisible,
        isStatic,
        position,
        scrollY,
        fixedPositionOffset,
        makeHidden,
        containerYOffset,
    ]);
    useOnValuesChange([topPassed, bottomPassed, scrollDirection, scrollYReached], handleValueChange);
    const parseHeightValues = useCallback((latestMakeHidden, latestPosition, latestHeaderHeight, latestOfferBarHeight, latestSubNavigationHeight, latestOffsetTopHeight, latestOffsetBottomHeight, latestTopPassed) => {
        const latestVariant = variant;
        const isFixed = latestPosition === HeaderPositions.fixed;
        // sync to be able to use that value for edge-cases
        baseHeaderHeight.set(latestHeaderHeight);
        if (latestVariant === HeaderVariants.default &&
            latestPosition === HeaderPositions.static &&
            latestTopPassed &&
            !!makeHidden.get()) {
            // update safeAreaHeight when the header is hidden after clicking on an anchor link
            setSafeAreaHeight(latestSubNavigationHeight || 0);
            setSubNavigationSafeAreaHeight(latestSubNavigationHeight || 0);
        }
        if (!!latestMakeHidden &&
            latestPosition === HeaderPositions.fixed &&
            latestVariant !== HeaderVariants.alwaysVisible &&
            latestVariant !== HeaderVariants.static) {
            // hide header
            makeHidden.set(true);
            setSafeAreaHeight(latestSubNavigationHeight || 0);
            setSubNavigationSafeAreaHeight(latestSubNavigationHeight || 0);
        }
        else if (latestVariant === HeaderVariants.static) {
            _headerHeight.set((!isFixed ? latestHeaderHeight : 0) + latestSubNavigationHeight || 0);
            setSafeAreaHeight((!isFixed ? latestHeaderHeight : 0) +
                (!isFixed ? latestOffsetTopHeight : 0) +
                (latestSubNavigationHeight || 0) +
                (!isFixed ? latestOffsetBottomHeight : 0));
            setSubNavigationSafeAreaHeight((!isFixed ? latestHeaderHeight : 0) +
                (!isFixed ? latestOffsetTopHeight : 0) +
                (latestSubNavigationHeight || 0));
        }
        else if (latestVariant === HeaderVariants.alwaysVisible) {
            _headerHeight.set(latestHeaderHeight + (latestSubNavigationHeight || 0));
            setSafeAreaHeight(latestHeaderHeight +
                (!isFixed && !latestTopPassed ? latestOffsetTopHeight : 0) +
                (latestSubNavigationHeight || 0) +
                // TODO: nodig voor desktop?
                (!isFixed && !latestTopPassed ? latestOffsetBottomHeight : 0));
            setSubNavigationSafeAreaHeight(latestHeaderHeight +
                (!isFixed && !latestTopPassed ? latestOffsetTopHeight : 0) +
                (latestSubNavigationHeight || 0));
        }
        else if (!latestMakeHidden) {
            // show header
            _headerHeight.set(latestHeaderHeight + (latestSubNavigationHeight || 0));
            setSafeAreaHeight(latestHeaderHeight +
                (!isFixed && !latestTopPassed ? latestOffsetTopHeight : 0) +
                (latestSubNavigationHeight || 0) +
                (!isFixed && !latestTopPassed ? latestOffsetBottomHeight : 0));
            setSubNavigationSafeAreaHeight(latestHeaderHeight +
                (!isFixed && !latestTopPassed ? latestOffsetTopHeight : 0) +
                (latestSubNavigationHeight || 0));
        }
        // important during anchoring + scrolling
        if (_headerHeight.get() === 0 && headerHeight.get() > 0) {
            _headerHeight.set(latestHeaderHeight + latestSubNavigationHeight || 0);
        }
        setOfferBarHeight(latestOfferBarHeight || 0);
    }, [
        _headerHeight,
        setOfferBarHeight,
        setSafeAreaHeight,
        headerHeight,
        variant,
        makeHidden,
        baseHeaderHeight,
        setSubNavigationSafeAreaHeight,
    ]);
    useOnValuesChange([
        makeHidden,
        position,
        headerHeight,
        offerBarHeight,
        subNavigationHeight,
        offsetTopHeight,
        offsetBottomHeight,
        topPassed,
    ], parseHeightValues);
    const themeType = useThemeType();
    const isCustomTheme = themeType === ThemeExtraTypes.custom;
    let ThemeWrapper = Fragment;
    let themeWrapperProps = {};
    if (!isCustomTheme) {
        ThemeWrapper = SwitchableThemeProvider;
        themeWrapperProps = {
            type: headerTheme === ThemeExtraTypes.inverted
                ? themeType === ThemeTypes.light
                    ? ThemeTypes.dark
                    : ThemeTypes.light
                : headerTheme || themeType,
        };
    }
    const hideOnScroll = !isStatic && !isAlwaysVisible;
    useEffect(() => scrollY.on('change', (val) => {
        const threshold = (scrollDeltaThreshold[mediaQueryName] || scrollDeltaThreshold.default) -
            spaceKeeperHeight.get();
        scrollPassedTopOffset.set(val > threshold
            ? `-${spaceKeeperHeight.get()}px`
            : // this 1px is needed to ensure that initially it is recognised to be passed when scrolling
                // visually it has no influence because it is only a tool to measure if it passed a certain point
                createCustomSpacing(1 / 16));
    }), [scrollY, mediaQueryName, scrollPassedTopOffset, fixedPositionOffset, spaceKeeperHeight]);
    /**
     * We place this here because the generate:index script for whatever reason can't find the Header export if we don't..
     */
    const top = (React.createElement(React.Fragment, null,
        !!offerBarProps && React.createElement(OfferBar, { ...offerBarProps, ref: offerBarRef, "data-type": "offer-bar" }),
        !isTransparent && React.createElement(Offset, { "data-type": "safe-area-top", ref: offsetTopRef }),
        !!offerBarProps && !isTransparent && !!headerHeight && (React.createElement(ScrolledPassedHelper, { offsetRef: topOffsetRef, passed: topPassed, "data-type": "scroll-passed-top" })),
        React.createElement(SpaceKeeper, { "data-type": "space-keeper", style: {
                display: fixedPositionOffset,
                height: spaceKeeperHeight,
            } }),
        offerBarProps === undefined && !isTransparent && !!headerHeight && (React.createElement(ScrolledPassedHelper, { offsetRef: topOffsetRef, passed: topPassed, "data-type": "scroll-passed-top", style: {
                top: scrollPassedTopOffset,
            } }))));
    const bottom = (React.createElement(React.Fragment, null, !isTransparent && React.createElement(Offset, { "data-type": "safe-area-bottom", ref: offsetBottomRef })));
    useEffect(() => makeHidden.on('change', (val) => {
        hidden.current = hideOnScroll && !!val;
        // move the menu part of the Header to the top and below if visible again
        containerYOffset.set(hideOnScroll && !!val
            ? // hide
                `-${isTransparent ? headerHeight.get() + offsetTopHeight.get() : headerHeight.get()}px`
            : isTransparent
                ? // show but a bit higher due to the inner offset
                    `-${offsetTopHeight.get()}px`
                : // show
                    '0');
        if (!isStatic && !isAlwaysVisible) {
            headerVisible.set(!val);
        }
    }), [
        containerYOffset,
        headerHeight,
        headerVisible,
        hideOnScroll,
        isAlwaysVisible,
        isStatic,
        isTransparent,
        makeHidden,
        offsetTopHeight,
    ]);
    // handle keyboard focus and show the header again
    const handleFocus = useCallback((e) => {
        // TODO: always show or different possible?
        if (hidden.current && isKeyboardControlled) {
            showHeader();
        }
        onFocus === null || onFocus === void 0 ? void 0 : onFocus(e);
    }, [hidden, showHeader, onFocus, isKeyboardControlled]);
    const subNavigationContent = useMemo(() => subNavigation.map(({ id, node, bleed }) => {
        if (!bleed) {
            return (React.createElement(HeaderSectionContainer, { key: id },
                React.createElement(HeaderSectionWrapper, null, node)));
        }
        return React.createElement(Fragment, { key: id }, node);
    }), [subNavigation]);
    return (React.createElement(ThemeWrapper, { ...themeWrapperProps },
        top,
        React.createElement(Container, { ...props, ref: ref, 
            // TODO: remove?
            enableTransition: false, isTransparent: isTransparent, style: {
                position: position,
                y: containerYOffset,
            }, onFocus: handleFocus },
            isTransparent && React.createElement(Offset, { "data-type": "safe-area-top", ref: offsetTopRef }),
            isTransparent && !!headerHeight && (React.createElement(ScrolledPassedHelper, { offsetRef: topOffsetRef, passed: topPassed, "data-type": "scroll-passed-top" })),
            React.createElement(StyledSection, { ref: callbackRef },
                React.createElement(Background, { "data-type": "background", style: {
                        opacity: animatedBackgroundOpacity,
                    } }),
                React.createElement(InnerWrapper, null,
                    React.createElement(ButtonContainer, { crossAxisAlignment: CrossAxisAlignments.center },
                        menuButton && (React.createElement(Fragment, null,
                            React.createElement(MenuButton, { ...menuButtonProps }),
                            !!actions && (React.createElement(Spacer, { spacing: Spacings.xLarge, orientation: Orientations.horizontal })))), (_a = (shouldRenderActions && actions)) !== null && _a !== void 0 ? _a : null),
                    React.createElement(StyledLogo, { ...logoProps }))),
            !!headerHeight && (React.createElement(ScrolledPassedHelper, { "data-type": "scroll-passed-bottom", offsetRef: bottomOffsetRef, passed: bottomPassed })),
            React.createElement(HeaderSubNavigationWrapper, { ref: subNavigationRef, "data-type": "sub-navigation-wrapper" }, subNavigationContent),
            isTransparent && React.createElement(Offset, { "data-type": "safe-area-bottom", ref: offsetBottomRef })),
        bottom));
});
Header.displayName = 'Header';
export default Header;
// necessary for the bleed options of the sub-navigation wrapper
const HeaderSectionContainer = styled.div `
  ${responsiveStyles(sectionContainerPaddings)}
`;
const HeaderSectionWrapper = styled.div `
  margin: 0 auto;

  ${responsiveStyles(sectionInnerStyling)}
`;
const HeaderSubNavigationWrapper = styled.div `
  position: relative;
  top: 0;
`;
const Offset = styled.div `
  ${responsiveStyles({
    height: {
        // do not follow 3vw * 2 on mobile while almost all applications use Spacings.small
        mobile: Spacings.small,
        tablet: Spacings.small,
        desktop: Spacings.large,
    },
})};
`;
export const HeaderTransparentMinimalOffset = {
    mobile: createCustomSpacing(72 / 16),
    tablet: createCustomSpacing(88 / 16),
    desktop: createCustomSpacing(120 / 16),
};
/**
 * Use this only (!) with "HeaderVariants.transparent" to ensure the content is pushed below the Header.
 * This can be a strict height, a responsive value or connected to the "safeAreaHeight" which changes when the Header is hidden (this might trigger page movements).
 *
 * When no offset is provided then the basic height adjustment is implemented. If a sub-navigation is added then set "minimalOffset" manually and use `HeaderTransparentMinimalOffset` as the basis for the new minimal offset. (use CSS calc to re-use the Header values in combination with the values). This cannot be done by this component because it does not know the safe values while the sub-navigation content can be any height and on the server-side (SSR) this cannot be calculated!
 */
export const HeaderTransparentOffset = forwardRef(({ minimalOffset, usingSafeArea }, ref) => {
    const { safeAreaHeight } = useHeader();
    if (minimalOffset === undefined) {
        // basic alignment without OfferBar or sub-navigation elements.
        minimalOffset = HeaderTransparentMinimalOffset;
    }
    // ensure this is only triggered when safeAreaHeight is known (after hydration at client-side)
    if (usingSafeArea && safeAreaHeight > 0) {
        minimalOffset = `${safeAreaHeight}px`;
    }
    return (React.createElement(TransparentOffset, { "data-type": "header-transparent-offset", ref: ref, offset: minimalOffset }));
});
const TransparentOffset = styled('div', shouldNotForwardProps(false, 'offset'))(({ offset }) => css(
// TODO: could be improved with transition but then the main header height should also be animated
typeof offset === 'string' &&
    css `
        height: ${offset};
      `, typeof offset !== 'string' &&
    css `
        ${responsiveStyles({ height: offset })};
      `));
const Container = styled(motion.header, shouldNotForwardProps(false, 'enableTransition', 'isTransparent'))(({ enableTransition, isTransparent }) => css(css `
      top: 0;
      width: 100%;
      z-index: ${LayoutIndexes.header};
      user-select: none;
      max-width: none;
    `, enableTransition &&
    css `
        /* TODO: only needed when scrolling up? not working? too late? due to Framer Motion ?*/
        transition: ${createCSSTransition('transform', MotionEasings.easeOut, MotionDurations.fast)};
      `, isTransparent &&
    css `
        height: 0;
      `));
const StyledSection = styled(Section) `
  overflow: hidden;
  width: 100%;
`;
const InnerWrapper = styled(motion.div) `
  display: flex;
  position: relative;
  align-items: center;
  ${responsiveStyles({
    padding: {
        mobile: Spacings.small,
        tablet: createCustomSpacing(1.25),
        desktop: Spacings.large,
    },
}, {
    padding: (value) => `${value} 0`,
})};
`;
const Background = styled(motion.div)(({ theme }) => css `
    content: '';
    position: absolute;
    inset-block-start: 0;
    inset-inline-end: -100vw;
    inset-block-end: 0;
    inset-inline-start: -100vw;
    transition: ${createCSSTransition('background-color', MotionEasings.easeOut, MotionDurations.fast)};
    background-color: ${theme.polestarApp.backgroundColor};
    z-index: 0;
  `);
const ButtonContainer = styled(Row) `
  height: ${Spacings.large};
  pointer-events: none;
  flex: 1;

  > * {
    pointer-events: auto;
  }
`;
const StyledLogo = styled(Logo) `
  margin: -${Spacings.xxSmall} 0;
  pointer-events: auto;
`;
